import { useEffect, useState } from "react";
import { listWallets, type WalletItem } from "@/api/wallets";
import { fetchPublicSettings } from "@/api/market";
import { errorMessage } from "@/api/auth";
import { buildPayUri, parsePayUri, type PayUri } from "@/pay/uri";
import { useSession } from "@/session/SessionContext";
import { useToast } from "@/toast/ToastContext";
import type { NetworkEnv } from "@/network/env";

export function PayPanel({ networkEnv }: { networkEnv: NetworkEnv }) {
  const { token } = useSession();
  const toast = useToast();
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState<PayUri | null>(null);
  const [wallets, setWallets] = useState<WalletItem[]>([]);
  const [recvWallet, setRecvWallet] = useState("");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState<"fixed" | "variable">("variable");
  const [link, setLink] = useState("");
  const [siteBase, setSiteBase] = useState("");

  useEffect(() => {
    fetchPublicSettings()
      .then((s) => setSiteBase((s.siteUrl || s.site_url || "").trim()))
      .catch(() => setSiteBase(""));
  }, []);

  useEffect(() => {
    if (!token) return;
    listWallets(token, networkEnv)
      .then((o) => setWallets(o.items || []))
      .catch(() => setWallets([]));
  }, [token, networkEnv]);

  function onParse() {
    const p = parsePayUri(raw);
    setParsed(p);
    if (!p) toast.err("PARSE");
  }

  function onBuild() {
    const w = wallets.find((x) => String(x.id) === recvWallet) || wallets[0];
    if (!w) {
      toast.err("WALLET");
      return;
    }
    try {
      const uri = buildPayUri({
        chain: w.chain,
        address: w.address,
        amount: amount || undefined,
        amountMode: mode,
        base: siteBase || undefined,
      });
      setLink(uri);
    } catch (e) {
      toast.err(errorMessage(e));
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.ok("کپی");
    } catch {
      toast.err("کپی");
    }
  }

  return (
    <section className="panel">
      <div className="card">
        <label>
          لینک / آدرس
          <input className="ltr" value={raw} onChange={(e) => setRaw(e.target.value)} />
        </label>
        <button type="button" className="primary" onClick={onParse}>
          خواندن
        </button>
        {parsed && (
          <div className="shops-cost ltr">
            <div>c={parsed.chain || "—"}</div>
            <div>a={parsed.address}</div>
            <div>
              t={parsed.asset} · n={parsed.amount || "—"} · m={parsed.amountMode}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <b>دریافت</b>
        <label>
          ولت
          <select
            className="ltr"
            value={recvWallet}
            onChange={(e) => setRecvWallet(e.target.value)}
          >
            <option value="">—</option>
            {wallets.map((w) => (
              <option key={w.id} value={String(w.id)}>
                {w.label || w.chain} · {w.address.slice(0, 10)}…
              </option>
            ))}
          </select>
        </label>
        <div className="mode-tabs">
          <button
            type="button"
            className={mode === "variable" ? "chip active" : "chip"}
            onClick={() => setMode("variable")}
          >
            متغیر
          </button>
          <button
            type="button"
            className={mode === "fixed" ? "chip active" : "chip"}
            onClick={() => setMode("fixed")}
          >
            ثابت
          </button>
        </div>
        <label>
          مبلغ
          <input className="ltr" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <button type="button" className="primary" onClick={onBuild} disabled={!token}>
          ساخت لینک
        </button>
        {link && (
          <div className="code-row">
            <code className="addr ltr">{link}</code>
            <button type="button" className="chip" onClick={() => void copy(link)}>
              کپی
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
